import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InteractiveChart from './InteractiveChart';
import ChartDirectorChart from './ChartDirectorChart';
import HoldingsTable from './HoldingsTable';
import OrderPanel from './OrderPanel';
import DraggableFab from './DraggableFab';
import TradesPage from './TradesPage';
import LeaderboardPage from './LeaderboardPage';
import VectorBTTransactionsPage from './VectorBTTransactionsPage';
import { apiFetch } from './api';
import './MainApp.css';

const EMPTY_ACCOUNT = {
  starting_capital: 100000,
  realised_pnl: 0,
  unrealised_pnl: 0,
  running_amount: 100000,
  wins: 0,
  losses: 0,
  closed_trades: 0,
  open_trades: 0,
  win_rate: 0,
  trades: [],
};

const PANEL_NUDGE_KEY = 'layoutPanelsUpHalfRowV1';

const numberText = (value, digits = 2) => {
  if (value === '' || value === null || value === undefined) return '—';
  return Number(value).toLocaleString('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const percentText = (value, digits = 2) => (
  value === '' || value === null || value === undefined
    ? '—'
    : `${numberText(value, digits)}%`
);

const volumeText = (value) => {
  if (value === '' || value === null || value === undefined) return '—';
  const n = Number(value);
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString('en-GB');
};

// Finviz's Market Cap field is stored in millions in the imported feed. Some
// rows carry it as a string, possibly already suffixed (e.g. "1.2B"), so parse
// everything into millions for both sorting and display.
const MARKET_CAP_SUFFIX_MILLIONS = { K: 1e-3, M: 1, B: 1e3, T: 1e6 };
const marketCapMillions = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const raw = String(value).trim().replace(/[$,]/g, '');
  const match = raw.match(/^(-?[\d.]+)\s*([KMBT])?$/i);
  if (!match) return null;
  const n = Number(match[1]);
  if (!Number.isFinite(n)) return null;
  return match[2] ? n * MARKET_CAP_SUFFIX_MILLIONS[match[2].toUpperCase()] : n;
};
const rowMarketCap = (row) => marketCapMillions(row?.actual_market_cap ?? row?.market_cap);

const marketCapText = (value) => {
  const m = marketCapMillions(value);
  if (m == null) return value === '' || value === null || value === undefined ? '—' : String(value);
  if (m >= 1e6) return `$${(m / 1e6).toFixed(2)}T`;
  if (m >= 1e3) return `$${(m / 1e3).toFixed(2)}B`;
  return `$${m.toFixed(2)}M`;
};

const atrPercent = (row) => {
  if (row?.atr === '' || row?.atr == null || row?.close === '' || row?.close == null) return null;
  const atr = Number(row?.atr);
  const currentPrice = Number(row?.close);
  if (!Number.isFinite(atr) || !(currentPrice > 0)) return null;
  return (atr / currentPrice) * 100;
};

const remainingAtrPercent = (row) => {
  const atrPct = atrPercent(row);
  if (row?.pct_up === '' || row?.pct_up == null) return null;
  const pctUp = Number(row?.pct_up);
  if (atrPct == null || !Number.isFinite(pctUp)) return null;
  return atrPct - Math.abs(pctUp);
};

// Bumping this discards every stored chartMode once, so a value written by
// the retired review auto-switch cannot masquerade as the user's own choice.
const CHART_MODE_PREF_VERSION = 3;
const LIVE_SCREEN_PREF_VERSION = 1;
const LIVE_DEFAULT_SCREEN = 'norgate_momentum';
// The same Norgate momentum membership, narrowed to names that are up today
// after one or two unusually quiet days.
const LIVE_COILED_SCREEN = 'norgate_coiled';
// The same membership, narrowed to names springing out of a 10MA coil: an
// extension clear of the line, a stretch tangled with it, then one big candle
// that reclaims it on heavier-than-normal volume.
const LIVE_SPRINGOUT_SCREEN = 'norgate_springout';

// Where live mode opens on a fresh login. Deliberately NOT LIVE_DEFAULT_SCREEN:
// that constant is also what training forces `screen` to, and training must
// keep trading the plain momentum list.
const LIVE_INITIAL_SCREEN = LIVE_SPRINGOUT_SCREEN;

// The live list is remembered per browser session rather than in the saved
// view preferences: a refresh comes back to the list you were on, while
// opening the app again later starts from LIVE_INITIAL_SCREEN.
const liveScreenSessionKey = (username) => `screenerLiveScreen:${String(username || '').trim().toLowerCase()}`;

const viewPreferenceKey = (username) => `screenerViewPreferences:${String(username || '').trim().toLowerCase()}`;

const readViewPreferences = (username) => {
  const isFinvizScreen = (value) => String(value || '').startsWith('finviz:');
  const isLiveScreen = (value) => value === LIVE_DEFAULT_SCREEN
    || value === LIVE_COILED_SCREEN || value === LIVE_SPRINGOUT_SCREEN
    || isFinvizScreen(value);
  const defaults = {
    market: 'US',
    mode: 'live',
    screen: LIVE_INITIAL_SCREEN,
    chartMode: null,
    chartPeriod: 'daily',
    liveScreen: '',
    liveShowWatchlist: false,
    liveShowOnlyUnticked: false,
  };
  let sessionScreen = '';
  try { sessionScreen = sessionStorage.getItem(liveScreenSessionKey(username)) || ''; } catch (_error) { /* private mode */ }
  try {
    const saved = JSON.parse(localStorage.getItem(viewPreferenceKey(username)) || '{}');
    // The Train/Live toggle is remembered across reloads. Only 'training' or
    // 'live' is accepted; anything else falls back to the live default.
    const savedMode = saved.mode === 'training' ? 'training' : 'live';
    return {
      market: 'US',
      mode: savedMode,
      // Training always trades the single norgate momentum list, so it never
      // restores a live (Finviz) screen. Live restores its remembered list.
      // Training always trades the plain momentum list. Live comes back to
      // whatever this browser session was last on, and starts from Springout
      // when the session is new.
      screen: savedMode === 'training'
        ? LIVE_DEFAULT_SCREEN
        : (isLiveScreen(sessionScreen) ? sessionScreen : LIVE_INITIAL_SCREEN),
      // `null` means "never chosen" - the caller falls back to the account
      // default. Anything stored under an older version is treated as never
      // chosen: while the review auto-switch existed it wrote 'chartdirector'
      // into this key behind the user's back, and that value would otherwise
      // look like a deliberate choice forever.
      chartMode: (saved.chartModeVersion === CHART_MODE_PREF_VERSION
        && ['interactive', 'chartdirector'].includes(saved.chartMode))
        ? saved.chartMode : null,
      chartPeriod: ['daily', 'monthly'].includes(saved.chartPeriod) ? saved.chartPeriod : defaults.chartPeriod,
      // The live screen and its filters, kept separately so a detour through
      // training does not destroy them (training forces screen to 'momentum').
      liveScreen: String(saved.liveScreen || '').startsWith('finviz:') ? saved.liveScreen : '',
      liveShowWatchlist: saved.liveShowWatchlist === true,
      liveShowOnlyUnticked: saved.liveShowOnlyUnticked === true,
    };
  } catch (_error) {
    return defaults;
  }
};

const blindSymbolText = (value) => {
  const symbol = String(value || '');
  if (!symbol) return '';
  if (symbol.length === 1) return `${symbol}...`;
  return `${symbol[0]}...${symbol[symbol.length - 1]}`;
};

const MainApp = ({ username, riskPerTrade = 200, trainingRiskPerTrade,
  tradingChartTv = true, liveAccess = false, onOpenProfile, onLogout }) => {
  const [initialViewPreferences] = useState(() => readViewPreferences(username));
  const [page, setPage] = useState('screener');
  const [orderOpen, setOrderOpen] = useState(true);
  const [date, setDate] = useState('');
  // A saved preference of 'live' outlives a revoked grant, so it is filtered
  // here as well; the server refuses either way, but landing on a mode that
  // errors on every request is not a useful starting state.
  const [mode, setMode] = useState(
    liveAccess ? initialViewPreferences.mode : 'training'
  );
  const [market, setMarket] = useState('US');
  const activeDataset = 'av';
  const [screen, setScreen] = useState(initialViewPreferences.screen);
  const [finvizGroups, setFinvizGroups] = useState([]);
  // Id of the closed trade being reviewed, or null when just trading.
  const [reviewOrderId, setReviewOrderId] = useState(null);
  const isFinvizScreen = String(screen || '').startsWith('finviz:');
  // Train → M selection, retained as the primary live list. Its membership
  // is calculated from Norgate; its chart stays on the AV + Finviz live path.
  const isNorgateCoiledScreen = screen === LIVE_COILED_SCREEN;
  const isNorgateSpringoutScreen = screen === LIVE_SPRINGOUT_SCREEN;
  // Coiled is a subset of momentum, so it shares every momentum behaviour
  // (chart path, live overlay, viewed-ticker marking) rather than being a
  // separate kind of list.
  const isNorgateMomentumScreen = screen === LIVE_DEFAULT_SCREEN || isNorgateCoiledScreen
    || isNorgateSpringoutScreen;
  const isLiveScreen = isFinvizScreen || isNorgateMomentumScreen;
  // Where live mode was left: the Finviz list and its two filters. Training
  // overwrites `screen`, so without this a mis-click on Train loses the list
  // and returning to Live drops you on the first group instead of your own.
  const liveViewRef = useRef({
    screen: initialViewPreferences.liveScreen,
    showWatchlist: initialViewPreferences.liveShowWatchlist,
    showOnlyUnticked: initialViewPreferences.liveShowOnlyUnticked,
  });
  const finvizGroup = isFinvizScreen ? screen.slice('finviz:'.length) : '';
  // Last date chosen from the calendar picker; the LstDt floating button
  // jumps back to it (survives reloads via localStorage).
  // Training date of the most recently PLACED trade (real-world order), which
  // is what LstDt jumps to in training. Falls back to lastPickedDate when the
  // account has no trades yet.
  const [lastTradeDate, setLastTradeDate] = useState('');
  const [lastPickedDate, setLastPickedDate] = useState(() => {
    try { return localStorage.getItem('screener2-last-picked-date') || ''; } catch { return ''; }
  });
  // One manual choice, and nothing in the app ever changes it - no auto-switch
  // on review, no per-mode override. The account default decides only where a
  // user who has never touched the toggle starts.
  const [chartMode, setChartMode] = useState(() => (
    initialViewPreferences.chartMode || (tradingChartTv ? 'interactive' : 'chartdirector')
  ));
  const [chartPeriod, setChartPeriod] = useState(initialViewPreferences.chartPeriod);
  const [showMovingAverages, setShowMovingAverages] = useState(
    () => localStorage.getItem('showTVMovingAverages') !== 'false'
  );
  const [rows, setRows] = useState([]);
  // Live overlay for norgate_momentum: today's Finviz-updated closes/moves
  // (us_screens refreshes them every minute). Kept separate from `rows` so a
  // refreshed price updates the display WITHOUT re-sorting the loaded order.
  const [livePricesBySymbol, setLivePricesBySymbol] = useState({});
  const [minDollarVolumeM, setMinDollarVolumeM] = useState('5');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'pct_up', direction: 'desc' });
  const [paperState, setPaperState] = useState({ session: null, orders: [], account: EMPTY_ACCOUNT });
  const [paperBusy, setPaperBusy] = useState(false);
  const [paperError, setPaperError] = useState('');
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('1');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [pickMode, setPickMode] = useState('');
  // The entry price of a holding you clicked, drawn on the chart. Gated on
  // the click rather than derived from the symbol, for the same reason
  // reviewOrderId is: paging the screener onto a name you happen to hold
  // should not start drawing trade lines over it.
  const [holdingEntryPrice, setHoldingEntryPrice] = useState('');
  const [ibkrSyncBusy, setIbkrSyncBusy] = useState(false);
  // The IBKR gateway is not left running: it is started for a sync and
  // stopped again, so the toolbar has to show which state it is in.
  const [ibkrGateway, setIbkrGateway] = useState(null);
  const [ibkrGatewayBusy, setIbkrGatewayBusy] = useState(false);
  // Real broker orders. ibkrTrading says whether the backend is pointed at the
  // paper or the live account, so the UI can never imply the wrong one.
  const [ibkrTrading, setIbkrTrading] = useState(null);
  const [ibkrAccount, setIbkrAccount] = useState('');
  const [ibkrOrderType, setIbkrOrderType] = useState('MKT');
  const [ibkrLimitPrice, setIbkrLimitPrice] = useState('');
  const [ibkrOrderBusy, setIbkrOrderBusy] = useState(false);
  const [ibkrOrderError, setIbkrOrderError] = useState('');
  const [ibkrOrderResult, setIbkrOrderResult] = useState('');
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistBusy, setWatchlistBusy] = useState(false);
  // Watchlist is now a normal option in the live-list selector. Keep this
  // state for compatibility with old saved view data, but do not restore the
  // retired standalone * view.
  const [showWatchlist, setShowWatchlist] = useState(false);
  // Holdings no longer collapses. The stored flag is cleared on load so a
  // panel collapsed by the old button cannot stay hidden with nothing left to
  // reopen it.
  useEffect(() => {
    try { localStorage.removeItem('screener2-holdings-collapsed'); } catch {}
  }, []);

  // Live only: tickers clicked today are marked "viewed" so they stand out
  // less. Keyed by date, so the set resets itself every day.
  const viewedKey = `screener2-viewed-${date}`;
  const [viewedSymbols, setViewedSymbols] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(`screener2-viewed-${date}`) || '[]'));
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    let loaded = new Set();
    try {
      loaded = new Set(JSON.parse(localStorage.getItem(viewedKey) || '[]'));
    } catch {}
    setViewedSymbols(loaded);
    if (mode !== 'live' || !date) return undefined;
    const controller = new AbortController();
    apiFetch(`/api/finviz/viewed?date=${encodeURIComponent(date)}`, { signal: controller.signal })
      .then((response) => response.ok ? response.json() : null)
      .then((body) => {
        if (!controller.signal.aborted && Array.isArray(body?.symbols)) {
          setViewedSymbols(new Set(body.symbols));
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [viewedKey, mode, date]);
  const toggleViewed = (symbol) => {
    const viewed = !viewedSymbols.has(symbol);
    setViewedSymbols((current) => {
      const next = new Set(current);
      if (viewed) next.add(symbol);
      else next.delete(symbol);
      try { localStorage.setItem(viewedKey, JSON.stringify([...next])); } catch {}
      return next;
    });
    apiFetch(`/api/finviz/viewed/${encodeURIComponent(symbol)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, viewed }),
    }).catch(() => {});
    // Marking a ticker viewed is the end of its review. Move directly to the
    // next visible ticker so the next chart is loaded without another click.
    if (viewed) {
      setLastTicked(symbol);
      selectNextAfterViewed(symbol);
    }
  };
  const undoLastTick = () => {
    if (!lastTicked) return;
    const symbol = lastTicked;
    setLastTicked(null);
    setViewedSymbols((current) => {
      const next = new Set(current);
      next.delete(symbol);
      try { localStorage.setItem(viewedKey, JSON.stringify([...next])); } catch {}
      return next;
    });
    apiFetch(`/api/finviz/viewed/${encodeURIComponent(symbol)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, viewed: false }),
    }).catch(() => {});
  };
  const newFilterKey = `screener2-show-only-unticked-${String(username || '').trim().toLowerCase()}`;
  const [showOnlyUnticked, setShowOnlyUnticked] = useState(() => {
    try { return localStorage.getItem(newFilterKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(newFilterKey, showOnlyUnticked ? '1' : '0'); } catch {}
  }, [newFilterKey, showOnlyUnticked]);
  // Live: prefix search over the loaded finviz list (e.g. "mr" -> MRNA...).
  const [tickerSearch, setTickerSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef(null);
  // Most recent ticker ticked as viewed, so the toolbar Undo can revert it.
  const [lastTicked, setLastTicked] = useState(null);
  const [marketDirection, setMarketDirection] = useState(null);

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;
    apiFetch(`/api/market-direction/${date}`)
      .then(async (response) => {
        if (!response.ok) throw new Error('Unavailable');
        return response.json();
      })
      .then((body) => { if (!cancelled) setMarketDirection(body); })
      .catch(() => { if (!cancelled) setMarketDirection(null); });
    return () => { cancelled = true; };
  }, [date]);

  const [mainSplitPct, setMainSplitPct] = useState(() => {
    const saved = Number(localStorage.getItem('layoutMainSplit'));
    return saved >= 25 && saved <= 75 ? saved : 55;
  });
  const [nudgePanelsOnLoad] = useState(() => localStorage.getItem(PANEL_NUDGE_KEY) !== '1');
  const [holdingsHeight, setHoldingsHeight] = useState(() => {
    const saved = Number(localStorage.getItem('layoutHoldingsHeight'));
    // 22px section heading + 22px table header + 8 * 22px rows.
    const defaultHeight = 224;
    const height = saved >= 100 && saved <= 360 ? saved : defaultHeight;
    return nudgePanelsOnLoad ? Math.max(100, height - 12) : height;
  });
  const workspaceRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const tableScrollRef = useRef(null);
  const screenerMemoryRef = useRef(new Map());
  const screenerMemoryStorageKey = `screener2ViewMemory:${String(username || '').trim().toLowerCase()}`;
  // Reload the per-view cursor memory (last viewed row + scroll position)
  // saved by previous sessions, so a refresh or revisit lands the cursor
  // back on the row you were viewing instead of restarting from the top.
  const hydrateScreenerMemory = useCallback(() => {
    if (screenerMemoryRef.current.size) return;
    try {
      const saved = JSON.parse(localStorage.getItem(screenerMemoryStorageKey) || '{}');
      Object.entries(saved).forEach(([key, value]) => screenerMemoryRef.current.set(key, value));
    } catch (_error) {}
  }, [screenerMemoryStorageKey]);
  const persistScreenerMemory = () => {
    try {
      const entries = [...screenerMemoryRef.current.entries()].slice(-60); // cap stored views
      const out = Object.fromEntries(entries);
      localStorage.setItem(screenerMemoryStorageKey, JSON.stringify(out));
    } catch (_error) {}
  };

  useEffect(() => {
    hydrateScreenerMemory();
  }, [hydrateScreenerMemory]);
  const tradesScrollTopRef = useRef(0);
  const hasActivePaperOrdersRef = useRef(false);
  const paperStateRef = useRef({ session: null, orders: [], account: EMPTY_ACCOUNT });
  const screenerViewKey = `${date}|${market}|${screen}`;
  const loadedViewKeyRef = useRef(screenerViewKey);

  useEffect(() => {
    if (!nudgePanelsOnLoad) return;
    localStorage.setItem('layoutHoldingsHeight', String(holdingsHeight));
    localStorage.setItem(PANEL_NUDGE_KEY, '1');
  }, [holdingsHeight, nudgePanelsOnLoad]);

  // Survives a refresh, not a new login - see liveScreenSessionKey.
  useEffect(() => {
    if (mode === 'training') return;
    try { sessionStorage.setItem(liveScreenSessionKey(username), screen); } catch (_error) { /* private mode */ }
  }, [username, mode, screen]);

  useEffect(() => {
    localStorage.setItem(viewPreferenceKey(username), JSON.stringify({
      mode,
      market,
      screen,
      chartMode,
      chartModeVersion: CHART_MODE_PREF_VERSION,
      liveScreenVersion: LIVE_SCREEN_PREF_VERSION,
      chartPeriod,
      liveScreen: liveViewRef.current.screen,
      liveShowWatchlist: liveViewRef.current.showWatchlist,
      liveShowOnlyUnticked: liveViewRef.current.showOnlyUnticked,
    }));
  }, [username, mode, market, screen, chartMode, chartPeriod,
      showWatchlist, showOnlyUnticked]);

  // The trade-derived LstDt target. paperState.orders is in the deps so this
  // refreshes as soon as a new trade is placed, rather than going stale until
  // the next reload.
  useEffect(() => {
    if (!username || mode !== 'training') return undefined;
    let cancelled = false;
    apiFetch(`/api/user/last-trade-date?dataset=${encodeURIComponent(activeDataset)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setLastTradeDate(body?.date || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [username, mode, activeDataset, paperState.orders]);

  // Hydrate the LstDt date from the server (per user, cross-device);
  // the local copy only stands in when the fetch fails.
  useEffect(() => {
    if (!username) return undefined;
    let cancelled = false;
    apiFetch('/api/user/last-date')
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const serverDate = body?.date || '';
        if (!cancelled && serverDate) {
          setLastPickedDate(serverDate);
          try { localStorage.setItem('screener2-last-picked-date', serverDate); } catch {}
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [username]);

  useEffect(() => {
    // Re-fetch whenever Live mode is entered so an empty/failed initial
    // load can never leave the Live list picker permanently empty.
    if (finvizGroups.length) return;
    apiFetch('/api/finviz/groups')
      .then((response) => (response.ok ? response.json() : []))
      .then((groups) => setFinvizGroups(Array.isArray(groups) ? groups : []))
      .catch(() => setFinvizGroups([]));
  }, [finvizGroups.length, isFinvizScreen]);

  useEffect(() => {
    localStorage.setItem('showTVMovingAverages', String(showMovingAverages));
  }, [showMovingAverages]);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/finviz/watchlist')
      .then((response) => (response.ok ? response.json() : { symbols: [] }))
      .then((body) => {
        if (!cancelled) setWatchlist(Array.isArray(body.symbols) ? body.symbols : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleWatchlist = async () => {
    const symbol = selectedSymbol.trim().toUpperCase();
    if (!symbol || watchlistBusy) return;
    const onList = watchlist.includes(symbol);
    setWatchlistBusy(true);
    try {
      const response = await apiFetch('/api/finviz/watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, action: onList ? 'remove' : 'add' }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to update watchlist');
      setWatchlist(Array.isArray(body.symbols) ? body.symbols : []);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setWatchlistBusy(false);
    }
  };

  useEffect(() => {
    localStorage.setItem('screenerWatchlistView', showWatchlist ? '1' : '0');
  }, [showWatchlist]);

  useEffect(() => {
    if (!showWatchlist) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    apiFetch('/api/finviz/watchlist', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load watchlist');
        return response.json();
      })
      .then((body) => {
        const liveRows = body.rows || [];
        const known = new Set(liveRows.map((row) => row.symbol));
        const extras = (body.symbols || [])
          .filter((symbol) => !known.has(symbol))
          .map((symbol) => ({ symbol }));
        const nextRows = [...liveRows, ...extras];
        loadedViewKeyRef.current = screenerViewKey;
        setRows(nextRows);
        setSelectedSymbol((current) => (
          nextRows.some((row) => row.symbol === current) ? current : nextRows[0]?.symbol || ''
        ));
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') {
          setRows([]);
          setError(requestError.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [showWatchlist, screenerViewKey, watchlist.length]);

  useEffect(() => {
    hasActivePaperOrdersRef.current = (paperState.orders || []).some((order) =>
      ['open', 'pending'].includes(order.status)
    );
  }, [paperState.orders]);

  const rememberScreenerView = (overrides = {}) => {
    const memoryKey = loadedViewKeyRef.current;
    const current = screenerMemoryRef.current.get(memoryKey) || {};
    screenerMemoryRef.current.set(memoryKey, {
      ...current,
      selectedSymbol,
      scrollTop: tableScrollRef.current?.scrollTop || 0,
      ...overrides,
    });
    persistScreenerMemory();
  };

  const startColResize = (event) => {
    event.preventDefault();
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    document.body.classList.add('resizing-col');
    const onMove = (moveEvent) => {
      const x = moveEvent.clientX - rect.left;
      const pct = Math.min(75, Math.max(25, (x / rect.width) * 100));
      setMainSplitPct(pct);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.classList.remove('resizing-col');
      setMainSplitPct((current) => {
        localStorage.setItem('layoutMainSplit', String(current));
        return current;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // fromBottom: the resized panel is the last row rather than the first, so
  // its height grows as the pointer moves up, not down.
  const makeRowResizeHandler = (panelRef, setSize, min, max, storageKey, fromBottom = false) => (event) => {
    event.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    document.body.classList.add('resizing-row');
    const onMove = (moveEvent) => {
      const distance = fromBottom ? rect.bottom - moveEvent.clientY : moveEvent.clientY - rect.top;
      const height = Math.min(max, Math.max(min, distance));
      setSize(height);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.classList.remove('resizing-row');
      setSize((current) => {
        localStorage.setItem(storageKey, String(current));
        return current;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startLeftRowResize = makeRowResizeHandler(leftPanelRef, setHoldingsHeight, 100, 360, 'layoutHoldingsHeight', true);

  // Default date on load. In Train mode, open on the day of the most recent
  // training trade (the active session's own current date as fallback) so the
  // user can manually forward dates from there. In Live mode, open on the
  // latest Finviz session date as before. Runs once per mode entry so manual
  // date forwarding in Train is never reset by later session-date changes.
  useEffect(() => {
    let cancelled = false;
    const dateGroup = isFinvizScreen ? finvizGroup : 'MomentumMerged';

    const fallbackToFinviz = (finvizPromise) => finvizPromise
      .then((dates) => {
        if (!cancelled && Array.isArray(dates) && dates.length) setDate(dates[0]);
      })
      .catch(() => setError('Could not load Finviz dates'));

    const finvizDates = () => apiFetch(`/api/finviz/dates?group=${encodeURIComponent(dateGroup)}`)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load Finviz dates');
        return response.json();
      });

    if (mode === 'training') {
      const sessionDate = paperStateRef.current?.session?.current_date;
      apiFetch(`/api/user/last-trade-date?dataset=${encodeURIComponent(activeDataset)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          if (cancelled) return undefined;
          // Prefer the most recent trade's date; fall back to the active
          // session's own current date; otherwise the latest Finviz date.
          const target = body?.date || sessionDate;
          if (target) setDate(target);
          else return fallbackToFinviz(finvizDates());
          return undefined;
        })
        .catch(() => {
          if (!cancelled) fallbackToFinviz(finvizDates());
        });
    } else {
      fallbackToFinviz(finvizDates());
    }
    return () => { cancelled = true; };
    // Run on mount and on each explicit mode change only - not on session-date
    // changes, so forwarding dates in Train is never reset underneath the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isFinvizScreen, finvizGroup, activeDataset]);

  // Keep a ref of the latest paper state so the initial-date picker can read
  // the active session's current date without re-running on session-date
  // changes (which would otherwise reset a manually forwarded Train date).
  useEffect(() => {
    paperStateRef.current = paperState;
  }, [paperState]);

  useEffect(() => {
    const cleanUsername = username.trim();
    if (!cleanUsername) {
      setPaperState({ session: null, orders: [], account: EMPTY_ACCOUNT });
      return undefined;
    }
     const controller = new AbortController();
    const timer = setTimeout(() => {
      setPaperBusy(true);
      setPaperError('');
      apiFetch(`/api/paper/state?mode=${mode}&dataset=${activeDataset}`, { signal: controller.signal })
        .then(async (response) => {
          const body = await response.json();
          if (!response.ok) throw new Error(body.error || 'Unable to load paper account');
          return body;
        })
        .then((body) => {
          localStorage.setItem('paperUsername', cleanUsername);
          setPaperState(body);
          const hasActiveOrders = (body.orders || []).some((order) =>
            ['open', 'pending'].includes(order.status)
          );
          hasActivePaperOrdersRef.current = hasActiveOrders;
          // Only the training backtest follows its session's date; live
          // finviz lists always jump to their own latest snapshot date.
          if (mode === 'training' && hasActiveOrders && body.session?.current_date) {
            setDate(body.session.current_date);
          }
        })
        .catch((requestError) => {
          if (requestError.name !== 'AbortError') setPaperError(requestError.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setPaperBusy(false);
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [username, mode, activeDataset]);

  // Show the gateway's real state on entering Live, so the button offers
  // Start or Stop to match a container this app did not necessarily start.
  useEffect(() => {
    if (mode !== 'live' || !username) return;
    refreshIbkrGateway();
    apiFetch('/api/paper/ibkr/trading')
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => body && setIbkrTrading(body))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, username]);

  // Accounts come from whichever gateway orders are sent to, not from synced
  // holdings: the paper gateway's account ids differ from the live ones.
  const ibkrAccounts = useMemo(() => (ibkrTrading?.accounts || []), [ibkrTrading]);

  useEffect(() => {
    if (!ibkrAccount && ibkrAccounts.length) setIbkrAccount(ibkrAccounts[0]);
  }, [ibkrAccounts, ibkrAccount]);

  // Live: poll paper state quietly so holdings P/L tracks the minute-cadence
  // iBeam price refreshes. Silent - no busy spinner, errors ignored.
  useEffect(() => {
    if (mode !== 'live' || !username) return undefined;
    const id = setInterval(async () => {
      try {
        const response = await apiFetch(`/api/paper/state?mode=live&dataset=av`);
        if (!response.ok) return;
        const body = await response.json();
        setPaperState(body);
        hasActivePaperOrdersRef.current = (body.orders || []).some((order) =>
          ['open', 'pending'].includes(order.status)
        );
      } catch {
        // next tick retries
      }
    }, 60000);
    return () => clearInterval(id);
  }, [mode, username, activeDataset]);

  useEffect(() => {
    if (showWatchlist || !date) return;
    const controller = new AbortController();
    setLoading(true);
    setGenerating(false);
    setError('');
    setRows([]);
    setSelectedSymbol('');

    const loadResults = async () => {
      if (isFinvizScreen) {
        const finvizResponse = await apiFetch(
          `/api/finviz/results/${encodeURIComponent(finvizGroup)}/${date}`,
          { signal: controller.signal }
        );
        const finvizBody = await finvizResponse.json();
        if (!finvizResponse.ok) throw new Error(finvizBody.error || 'Unable to load Finviz results');
        return finvizBody;
      }

      // The non-Finviz lists in this live app are norgate_momentum and the
      // coiled subset of it. The former M/P training screens are
      // intentionally not reachable here.
      const screenName = screen === LIVE_COILED_SCREEN ? 'coiled'
        : screen === LIVE_SPRINGOUT_SCREEN ? 'springout'
        : 'momentum';
      const screenDataset = 'norgate';
      let response = await apiFetch(
        `/api/screener/results/${market}/${screenName}/${date}?dataset=${screenDataset}`,
        { signal: controller.signal }
      );

      if (response.status === 404) {
        setGenerating(true);
        const generateResponse = await apiFetch(
          `/api/screener/generate/${date}`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dataset: screenDataset }),
            signal: controller.signal }
        );
        if (!generateResponse.ok) {
          const body = await generateResponse.json().catch(() => ({}));
          throw new Error(body.error || `Unable to generate results for ${date}`);
        }
        setGenerating(false);
        response = await apiFetch(
          `/api/screener/results/${market}/${screenName}/${date}?dataset=${screenDataset}`,
          { signal: controller.signal }
        );
      }

      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to load results');
      return body;
    };

    loadResults()
      .then((payload) => {
        const nextRows = payload.rows || [];
        loadedViewKeyRef.current = screenerViewKey;
        setRows(nextRows);
        setSelectedSymbol(() => {
          const rememberedSymbol = screenerMemoryRef.current.get(screenerViewKey)?.selectedSymbol;
          if (rememberedSymbol && nextRows.some((row) => row.symbol === rememberedSymbol)) {
            return rememberedSymbol;
          }
          const firstBySl = [...nextRows].sort((a, b) => {
            const av = remainingAtrPercent(a) ?? Infinity;
            const bv = remainingAtrPercent(b) ?? Infinity;
            return av - bv;
          })[0];
          return firstBySl?.symbol || '';
        });
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') {
          setRows([]);
          setSelectedSymbol('');
          setError(requestError.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setGenerating(false);
        }
      });
    return () => controller.abort();
  }, [date, market, screen, screenerViewKey, isFinvizScreen, finvizGroup, showWatchlist]);

  // Live screens poll quietly so fresh us_screens prices show up without a
  // manual refresh. Deliberately silent: no loading state, selection kept.
  useEffect(() => {
    if (!isFinvizScreen || !date || showWatchlist) return undefined;
    const id = setInterval(async () => {
      try {
        const response = await apiFetch(`/api/finviz/results/${encodeURIComponent(finvizGroup)}/${date}`);
        if (!response.ok) return;
        const body = await response.json();
        const nextRows = body.rows || [];
        // Merge new prices into the CURRENT row order so rows don't jump
        // around mid-session; genuinely new tickers go to the bottom.
        setRows((currentRows) => {
          const bySymbol = new Map(nextRows.map((row) => [row.symbol, row]));
          const merged = currentRows.map((row) => bySymbol.get(row.symbol) || row);
          const known = new Set(currentRows.map((row) => row.symbol));
          nextRows.forEach((row) => {
            if (!known.has(row.symbol)) merged.push(row);
          });
          return merged;
        });
        setSelectedSymbol((current) => (
          current && nextRows.some((row) => row.symbol === current) ? current : ''
        ));
      } catch {
        // Poll failures are invisible - the next tick retries.
      }
    }, 60000);
    return () => clearInterval(id);
  }, [isFinvizScreen, finvizGroup, date, showWatchlist]);

  // norgate_momentum live overlay: norgate closes lag by a day, so for the
  // current trading day we poll the same screen result (which the backend
  // enriches with today's Finviz/us_screens values) and overlay just the
  // price fields onto the CURRENT row order. No re-sort: the sort stays based
  // on the EOD norgate close until the user explicitly refreshes.
  useEffect(() => {
    if (!isNorgateMomentumScreen || !date || showWatchlist) return undefined;
    const today = new Date().toISOString().slice(0, 10);
    if (date !== today) {
      setLivePricesBySymbol({});
      return undefined;
    }
    const id = setInterval(async () => {
      try {
        const response = await apiFetch(
          `/api/screener/results/US/momentum/${date}?dataset=norgate`,
          { signal: AbortSignal.timeout(60000) }
        );
        if (!response.ok) return;
        const body = await response.json();
        const overlay = {};
        (body.rows || []).forEach((row) => {
          if (!row.symbol) return;
          overlay[row.symbol] = {
            close: row.close,
            pct_up: row.pct_up,
            dollar_up: row.dollar_up,
            volume: row.volume,
          };
        });
        setLivePricesBySymbol(overlay);
      } catch {
        // Silent: a failed poll keeps the last good overlay until next tick.
      }
    }, 60000);
    return () => {
      clearInterval(id);
      setLivePricesBySymbol({});
    };
  }, [isNorgateMomentumScreen, date, showWatchlist]);

  useEffect(() => {
    if (page !== 'screener' || !tableScrollRef.current) return;
    const remembered = screenerMemoryRef.current.get(screenerViewKey);
    tableScrollRef.current.scrollTop = remembered?.scrollTop || 0;
  }, [page, rows, screenerViewKey]);

  const requestDate = async (nextDate) => {
    if (!nextDate || nextDate === date) return;
    const sessionDate = paperState.session?.current_date;
    // The paper session only advances in training. Live finviz/momentum lists
    // are snapshots, not part of the backtest, so browsing their dates never
    // moves the session.
    if (mode === 'training' && sessionDate && nextDate !== sessionDate) {
      setPaperBusy(true);
      setPaperError('');
      try {
        const response = await apiFetch('/api/paper/advance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: username.trim(), date: nextDate, dataset: activeDataset }),
        });
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'Unable to change the paper-session date');
        setPaperState(body);
      } catch (requestError) {
        setPaperError(requestError.message);
        return;
      } finally {
        setPaperBusy(false);
      }
    }
    setDate(nextDate);
  };

  const moveDate = async (days) => {
    if (!date || paperBusy) return;
    setPaperError('');
    try {
      const direction = days > 0 ? 'next' : 'previous';
      const endpoint = (mode === 'live')
        ? `/api/finviz/trading-date/${direction}/${date}?group=${encodeURIComponent(isFinvizScreen ? finvizGroup : 'MomentumMerged')}`
        : `/api/screener/trading-date/${market}/${direction}/${date}?dataset=${activeDataset}`;
      const response = await apiFetch(endpoint);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || `Unable to find the ${direction} trading date`);
      // Live finviz/norgate lists are live snapshots, not part of the paper
      // backtest — move the view without touching the session's own date.
      if (mode === 'live') {
        setDate(body.date);
        return;
      }
      // Training: advance the paper session so the backtest rebuilds prices,
      // entries, and stops for the requested date.
      await requestDate(body.date);
    } catch (requestError) {
      setPaperError(requestError.message);
    }
  };

  const fromLowField = 'pct_from_low_1mth';
  const minDollarVolume = useMemo(() => {
    const n = Number(minDollarVolumeM);
    return minDollarVolumeM !== '' && Number.isFinite(n) && n > 0 ? n * 1e6 : null;
  }, [minDollarVolumeM]);
  // Rows with today's live values applied, so the sort uses the same numbers
  // that are on screen (norgate lists get these from the 60s overlay; finviz
  // lists already carry them in `rows`).
  const liveRows = useMemo(() => {
    if (!isNorgateMomentumScreen || !Object.keys(livePricesBySymbol).length) return rows;
    return rows.map((row) => (
      livePricesBySymbol[row.symbol] ? { ...row, ...livePricesBySymbol[row.symbol] } : row
    ));
  }, [rows, isNorgateMomentumScreen, livePricesBySymbol]);
  const hasLiveOverlay = isNorgateMomentumScreen && Object.keys(livePricesBySymbol).length > 0;

  const liveSortedRows = useMemo(() => {
    const filtered = minDollarVolume == null
      ? liveRows
      : liveRows.filter((row) => Number(row.dollar_volume) >= minDollarVolume);
    return [...filtered].sort((a, b) => {
      const key = sort.key === 'pct_from_low' ? fromLowField : sort.key;
      const valueOf = (row) => {
        if (key === 'sl') return remainingAtrPercent(row);
        if (key === 'market_cap') return rowMarketCap(row);
        return row[key];
      };
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av === '' || av === null || av === undefined) return 1;
      if (bv === '' || bv === null || bv === undefined) return -1;
      let comparison = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));
      if (key === 'sl') comparison *= -1;
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [liveRows, sort, fromLowField, minDollarVolume]);

  // On every screen the order is taken when the list loads, when a header is
  // clicked, or when live values first arrive - and then held, so the 60s
  // refreshes update the numbers without moving rows. New tickers go last.
  const [frozenOrder, setFrozenOrder] = useState(null);
  const [sortNonce, setSortNonce] = useState(0);
  useEffect(() => {
    setFrozenOrder(null);
  }, [screen, date, finvizGroup, showWatchlist, sort, sortNonce, minDollarVolume, hasLiveOverlay]);
  useEffect(() => {
    if (frozenOrder || loading || liveSortedRows.length === 0) return;
    setFrozenOrder(liveSortedRows.map((row) => row.symbol));
  }, [frozenOrder, loading, liveSortedRows]);
  const sortedRows = useMemo(() => {
    if (!frozenOrder) return liveSortedRows;
    const position = new Map(frozenOrder.map((symbol, i) => [symbol, i]));
    const end = frozenOrder.length;
    return [...liveSortedRows].sort((a, b) => (
      (position.has(a.symbol) ? position.get(a.symbol) : end)
      - (position.has(b.symbol) ? position.get(b.symbol) : end)
    ));
  }, [liveSortedRows, frozenOrder]);

  // Live ticker search: queries the FULL candle universe from the backend,
  // independent of whichever finviz list happens to be loaded.
  const [searchResults, setSearchResults] = useState([]);
  useEffect(() => {
    const query = tickerSearch.trim();
    if (!query) {
      setSearchResults([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await apiFetch(
          `/api/screener/symbol-search?q=${encodeURIComponent(query)}&dataset=${activeDataset}&_=${Date.now()}`,
          { signal: controller.signal }
        );
        if (!response.ok) return;
        const body = await response.json();
        setSearchResults(body.symbols || []);
      } catch {
        // ignore - dropdown just stays empty until next keystroke
      }
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [tickerSearch]);

  // Reported by the backend per row; taken from the data rather than
  // recomputed here so the label cannot disagree with the numbers.
  const coilAsOf = rows.find((row) => row.coil_as_of)?.coil_as_of || '';
  const selectedRow = rows.find((row) => row.symbol === selectedSymbol) || null;
  // Today's move (%Up) for each screener symbol, used to annotate holdings.
  const pctUpBySymbol = useMemo(() => {
    const map = {};
    rows.forEach((row) => {
      if (row.symbol) map[row.symbol] = row.pct_up;
    });
    return map;
  }, [rows]);
  // Live "New" filter: when on, only today's unticked tickers are visible,
  // in the table and in Prev/Next navigation.
  const visibleRows = useMemo(() => {
    let list = sortedRows;
    if (isLiveScreen && showOnlyUnticked) {
      list = list.filter((row) => !viewedSymbols.has(row.symbol));
    }
    return list;
  }, [sortedRows, isLiveScreen, showOnlyUnticked, viewedSymbols]);

  // Overlay today's live Finviz values onto the sorted rows in place. The
  // sort key always uses the EOD norgate values (from `visibleRows`), so
  // refreshed prices never reorder the list.
  const displayRows = useMemo(() => {
    if (isNorgateMomentumScreen && Object.keys(livePricesBySymbol).length) {
      return visibleRows.map((row) =>
        livePricesBySymbol[row.symbol] ? { ...row, ...livePricesBySymbol[row.symbol] } : row
      );
    }
    return visibleRows;
  }, [visibleRows, isNorgateMomentumScreen, livePricesBySymbol]);

  const selectNextAfterViewed = (symbol) => {
    const currentIndex = visibleRows.findIndex((row) => row.symbol === symbol);
    const nextRow = currentIndex >= 0 ? visibleRows[currentIndex + 1] : null;
    if (!nextRow) return;
    selectScreenerSymbol(nextRow.symbol);
    window.requestAnimationFrame(() => {
      const container = tableScrollRef.current;
      const row = container?.querySelectorAll('tbody tr')?.[currentIndex + 1];
      row?.scrollIntoView({ block: 'nearest' });
    });
  };
  const selectedSortedIndex = visibleRows.findIndex((row) => row.symbol === selectedSymbol);
  const account = paperState.account || EMPTY_ACCOUNT;
  // Train allows one position per symbol; live lets you add to the same stock.
  const alreadyOwned = Boolean(selectedSymbol) && mode === 'training' && (paperState.orders || []).some((order) => (
    order.symbol === selectedSymbol && order.market === market && ['open', 'pending'].includes(order.status)
  ));

  // Separate risk defaults: training uses its own value when set.
  const activeRiskPerTrade = mode === 'training'
    ? (Number(trainingRiskPerTrade) || riskPerTrade)
    : riskPerTrade;

  const riskSizedQuantity = useCallback((stop, entryOverride) => {
    const entry = Number(entryOverride ?? (orderType === 'limit' ? limitPrice : selectedRow?.close));
    const stopNumber = Number(stop);
    const risk = Number(activeRiskPerTrade);
    if (!(entry > stopNumber) || !(risk > 0)) return '';
    const rawQuantity = risk / (entry - stopNumber);
    const sizedQuantity = Math.floor(rawQuantity);
    return sizedQuantity > 0 ? String(sizedQuantity) : '';
  }, [limitPrice, orderType, activeRiskPerTrade, selectedRow?.close]);

  useEffect(() => {
    if (stopPrice === '') return;
    setQuantity(riskSizedQuantity(stopPrice));
  }, [riskSizedQuantity, stopPrice]);

  // Live orders are always limits - never fall back to market.
  useEffect(() => {
    if (mode === 'live' && orderType !== 'limit') setOrderType('limit');
  }, [mode, orderType]);

  // In live mode the limit entry always mirrors the current price of the
  // selected ticker - including when the ticker was already selected before
  // switching to live, or restored after a page reload.
  useEffect(() => {
    if (mode !== 'live') return;
    const close = selectedRow?.close;
    setLimitPrice(close != null ? String(close) : '');
  }, [mode, selectedRow?.symbol, selectedRow?.close]);

  const selectScreenerSymbol = (symbol) => {
    setReviewOrderId(null);
    const nextRow = rows.find((row) => row.symbol === symbol) || null;
    setSelectedSymbol(symbol);
    rememberScreenerView({ selectedSymbol: symbol });
    // Live orders are always limits resting at the current price.
    setLimitPrice(mode === 'live' && nextRow?.close != null ? String(nextRow.close) : '');
    setStopPrice('');
    setHoldingEntryPrice('');
    setPickMode('');
    // An "already owned" (or any other) order error is specific to whatever
    // symbol it was raised for - carrying it over to a newly selected row
    // would be actively misleading, so clear it on every navigation.
    setPaperError('');
    if (mode === 'training') setOrderType('market');
    else setOrderType('limit');
  };

  const moveScreenerSelection = (step) => {
    if (!visibleRows.length) return;
    const currentIndex = selectedSortedIndex >= 0
      ? selectedSortedIndex
      : (step > 0 ? -1 : visibleRows.length);
    const nextIndex = Math.max(0, Math.min(visibleRows.length - 1, currentIndex + step));
    if (nextIndex === selectedSortedIndex) return;
    selectScreenerSymbol(visibleRows[nextIndex].symbol);
    window.requestAnimationFrame(() => {
      const container = tableScrollRef.current;
      const row = container?.querySelectorAll('tbody tr')?.[nextIndex];
      if (!container || !row) return;
      // Center the selected row in the visible area below the sticky header
      // (rather than just nudging it into view at an edge), so Next/Prev
      // always lands somewhere obviously in focus, not right at the top/
      // bottom edge where it's easy to miss.
      const headerHeight = container.querySelector('thead')?.offsetHeight || 0;
      const rowCenter = row.offsetTop + row.offsetHeight / 2;
      const visibleHeight = container.clientHeight - headerHeight;
      const desiredScrollTop = rowCenter - headerHeight - visibleHeight / 2;
      const maxScrollTop = container.scrollHeight - container.clientHeight;
      container.scrollTop = Math.max(0, Math.min(maxScrollTop, desiredScrollTop));
    });
  };

  const handlePricePick = (pickTarget, price) => {
    if (pickTarget === 'entry') {
      setOrderType('limit');
      setLimitPrice(String(price));
    } else if (pickTarget === 'stop') {
      setStopPrice(String(price));
      setQuantity(riskSizedQuantity(price));
    } else if (String(pickTarget).startsWith('stop:')) {
      // Picking a stop for an existing order straight from the holdings table.
      updateOrderStop(String(pickTarget.slice('stop:'.length)), String(price));
    }
    setPickMode('');
  };

  const placeOrder = async () => {
    const wholeQuantity = Math.floor(Number(quantity));
    if (!(wholeQuantity > 0)) {
      setPaperError('Quantity must be a positive whole number');
      return;
    }
    setQuantity(String(wholeQuantity));
    setPaperBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch('/api/paper/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(), mode, market, symbol: selectedSymbol, date,
          orderType, quantity: wholeQuantity, limitPrice, stopPrice, dataset: activeDataset,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to place paper order');
      setPaperState(body);
      setLimitPrice('');
      setStopPrice('');
      setPickMode('');
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  const refreshIbkrGateway = async () => {
    try {
      const response = await apiFetch('/api/paper/ibkr/gateway');
      const body = await response.json();
      if (response.ok) setIbkrGateway(body);
    } catch {
      // A status read is only for the badge; never surface it as an error.
    }
  };

  const toggleIbkrGateway = async () => {
    const stopping = ibkrGateway?.running;
    setIbkrGatewayBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch(`/api/paper/ibkr/gateway/${stopping ? 'stop' : 'start'}`, {
        method: 'POST',
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to change the IBKR gateway');
      setIbkrGateway(body);
    } catch (requestError) {
      setPaperError(requestError.message);
      await refreshIbkrGateway();
    } finally {
      setIbkrGatewayBusy(false);
    }
  };

  const placeIbkrOrder = async (side) => {
    const wholeQuantity = Math.floor(Number(quantity));
    if (!(wholeQuantity > 0)) return;
    const isLive = ibkrTrading?.target === 'live';
    const priceText = ibkrOrderType === 'LMT' ? `at ${ibkrLimitPrice}` : 'at market';
    // Real money deserves a stop, and the wording names the account and the
    // paper/live target so a mis-pointed backend is obvious before sending.
    const confirmed = window.confirm(
      `${isLive ? 'REAL MONEY ORDER\n\n' : 'Paper account order\n\n'}`
      + `${side} ${wholeQuantity} ${selectedSymbol} ${priceText}\n`
      + `Account: ${ibkrAccount}\n\nSend this to IBKR?`
    );
    if (!confirmed) return;

    setIbkrOrderBusy(true);
    setIbkrOrderError('');
    setIbkrOrderResult('');
    try {
      const body = {
        symbol: selectedSymbol,
        side,
        quantity: wholeQuantity,
        orderType: ibkrOrderType,
        price: ibkrOrderType === 'LMT' ? Number(ibkrLimitPrice) : undefined,
        accountId: ibkrAccount,
        // Identifies this click, so a double-press is a duplicate IBKR
        // rejects rather than a second live order.
        clientOrderId: `sl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
      let response = await apiFetch('/api/paper/ibkr/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      let result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to place the order');

      // IBKR can answer with warnings (margin, size, off-hours). They are put
      // to you verbatim instead of being auto-accepted.
      while (result.status === 'needs_confirmation') {
        const accept = window.confirm(`IBKR asks:\n\n${(result.questions || []).join('\n\n')}\n\nConfirm and send?`);
        if (!accept) {
          setIbkrOrderResult('Order not sent.');
          return;
        }
        response = await apiFetch('/api/paper/ibkr/order/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ replyId: result.replyId }),
        });
        result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Unable to confirm the order');
      }
      setIbkrOrderResult(result.status === 'submitted'
        ? `Sent to ${result.target}: order ${result.orderId}${result.orderStatus ? ` (${result.orderStatus})` : ''}`
        : `IBKR replied: ${result.status}`);
    } catch (requestError) {
      setIbkrOrderError(requestError.message);
    } finally {
      setIbkrOrderBusy(false);
    }
  };

  const syncIbkrHoldings = async () => {
    setIbkrSyncBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch('/api/paper/ibkr/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataset: activeDataset }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to sync IBKR holdings');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setIbkrSyncBusy(false);
      // A sync starts the gateway if it was down, so the badge is now stale.
      refreshIbkrGateway();
    }
  };

  const closeOrder = async (orderId, reason, closeQuantity) => {
    setPaperBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch(`/api/paper/orders/${orderId}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), reason, quantity: closeQuantity }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to close holding');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  // Releasing a reviewed trade is server-side state (orders.released_at), so
  // it holds across devices - the same account on a phone no longer sees
  // trades already released on the desktop. Note and release are one request.
  const releaseOrder = async (orderId, note) => {
    const response = await apiFetch(`/api/paper/orders/${orderId}/release`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim(), note: note || null }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Unable to release the trade');
    setPaperState((current) => ({
      ...current,
      orders: (current.orders || []).map((order) => (
        order.id === orderId
          ? { ...order, released_at: body.released_at || new Date().toISOString(),
              journal_note: note || order.journal_note }
          : order
      )),
    }));
  };


  const updateOrderStop = async (orderId, nextStopPrice) => {
    setPaperBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch(`/api/paper/orders/${orderId}/stop`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), stopPrice: nextStopPrice }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to update stop loss');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  const cancelOrder = async (orderId) => {
    setPaperBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch(`/api/paper/orders/${orderId}`, { method: 'DELETE' });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to cancel order');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  const resetPaperSession = async () => {
    if (!window.confirm(
      'WARNING: resetting archives the current session, closes its open holdings, '
      + `and permanently deletes ALL trade history for "${username}" in ${mode} mode. `
      + 'This cannot be undone. Continue?'
    )) return;
    const password = window.prompt('Enter your password to confirm the reset:');
    if (!password) return;
    setPaperBusy(true);
    setPaperError('');
    try {
      const check = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim().toLowerCase(), password }),
      });
      if (!check.ok) throw new Error('Password incorrect — reset cancelled');
      const response = await apiFetch('/api/paper/reset', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), mode, dataset: activeDataset }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to reset paper session');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  const fillOrder = async (orderId, fillPrice, quantity, stopPrice) => {
    setPaperBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch(`/api/paper/orders/${orderId}/fill`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fillPrice: fillPrice || undefined,
          quantity,
          stopPrice: stopPrice || undefined,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to confirm fill');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  const updateOrderEntry = async (orderId, entryPrice) => {
    setPaperBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch(`/api/paper/orders/${orderId}/entry`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryPrice }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to update entry price');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  const updateOrderQty = async (orderId, quantity) => {
    setPaperBusy(true);
    setPaperError('');
    try {
      const response = await apiFetch(`/api/paper/orders/${orderId}/quantity`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to update quantity');
      setPaperState(body);
    } catch (requestError) {
      setPaperError(requestError.message);
    } finally {
      setPaperBusy(false);
    }
  };

  // Entry/exit fill points for the symbol currently on the chart, so a closed
  // trade can be reviewed against the candles it was actually bought and sold
  // on. ChartDirectorChart already knows how to draw these (it maps a
  // date+price onto the plot geometry the renderer returns) - it was simply
  // never given any. Partial exits are included and labelled separately, since
  // a position scaled out of has more than one meaningful exit candle.
  // LstDt is deliberately based only on the most recent training purchase.
  // Calendar picks and Live trades must never change this target.
  const lstDtTarget = lastTradeDate;

  const transactionMarkers = useMemo(() => {
    if (!reviewOrderId || !selectedSymbol) return [];
    const symbol = selectedSymbol.trim().toUpperCase();
    const chartMarket = market;
    const markers = [];
    for (const order of paperState.orders || []) {
      if (String(order.symbol || '').trim().toUpperCase() !== symbol) continue;
      if (order.market !== chartMarket) continue;
      if (order.entry_date && Number(order.entry_price) > 0) {
        markers.push({
          kind: 'entry',
          date: order.entry_date,
          price: Number(order.entry_price),
          label: 'Buy',
        });
      }
      if (order.exit_date && Number(order.exit_price) > 0) {
        markers.push({
          kind: 'exit',
          date: order.exit_date,
          price: Number(order.exit_price),
          label: order.partial_close ? 'Part' : 'Exit',
        });
      }
    }
    // Scaling into a position writes several orders sharing one entry date and
    // price (AIFA in the current data has four), which would stack identical
    // labels on the same candle. Collapse exact duplicates; genuinely distinct
    // fills - a different date or a different price - are all kept, because
    // that is the thing being reviewed.
    const seen = new Set();
    return markers.filter((marker) => {
      const key = `${marker.kind}|${marker.date}|${marker.price}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [reviewOrderId, selectedSymbol, market, paperState.orders]);

  // Full chart history is loaded for one case only: a symbol on screen that has
  // an actual exit, i.e. a closed trade being reviewed. Everything else -
  // screener rows, open positions, normal training - keeps the tighter capped
  // window, so the learning view is unchanged.
  const reviewingClosedTrade = useMemo(
    () => transactionMarkers.some((marker) => marker.kind === 'exit'),
    [transactionMarkers]
  );
  // The chart is bounded by the date selected on top. When a closed trade is
  // being reviewed from a date *before* its exit - go back to the entry day to
  // study the setup, say - the exit candle falls outside that bound and never
  // loads. Hand the backend the latest exit so it can extend the ceiling.
  // Earliest entry of the trade under review. The chart floor is measured back
  // from this, not from the selected date, so the review always opens with the
  // run-up into the buy visible.
  const reviewStartDate = useMemo(() => {
    const entries = transactionMarkers
      .filter((marker) => marker.kind === 'entry' && marker.date)
      .map((marker) => String(marker.date).slice(0, 10));
    return entries.length ? entries.sort()[0] : '';
  }, [transactionMarkers]);
  const reviewEndDate = useMemo(() => {
    const exits = transactionMarkers
      .filter((marker) => marker.kind === 'exit' && marker.date)
      .map((marker) => String(marker.date).slice(0, 10));
    return exits.length ? exits.sort().at(-1) : '';
  }, [transactionMarkers]);

  const selectHolding = (order) => {
    // Reviewing is an explicit act - clicking a closed trade - not merely
    // "this symbol has a trade somewhere". Deriving it from the symbol meant
    // paging through the screener in training could land on a previously
    // traded name and silently switch the chart into review, which also
    // extended the window forward to that trade's exit: future candles, while
    // training. Gate it on the click instead.
    setReviewOrderId(
      order.status === 'closed' && !order.partial_close ? order.id : null
    );
    setMarket(order.market);
    setSelectedSymbol(order.symbol);
    setOrderType('limit');
    setLimitPrice(String(order.entry_price ?? order.limit_price ?? ''));
    setStopPrice(String(order.stop_price ?? ''));
    setHoldingEntryPrice(String(order.entry_price ?? ''));
    setPickMode('');
  };

  const changeSort = (key) => {
    setSortNonce((n) => n + 1);
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortMark = (key) => {
    if (sort.key !== key) return '';
    return sort.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const changePage = (nextPage) => {
    if (page === 'screener') rememberScreenerView();
    setPage(nextPage);
  };

  const changeScreen = (nextScreen) => {
    setReviewOrderId(null);
    rememberScreenerView();
    setScreen(nextScreen);
    if (String(nextScreen || '').startsWith('finviz:')) setMarket('US');
  };

  const changeMode = (nextMode) => {
    if (nextMode === mode) return;
    if (nextMode === 'live' && !liveAccess) return;
    setReviewOrderId(null);
    rememberScreenerView();
    setPaperError('');
    // Bank the live list + its filters before a detour through training, so
    // returning to Live drops you back where you left off instead of the
    // first list in the dropdown.
    if (mode === 'live') {
      liveViewRef.current = {
        screen: String(screen || '').startsWith('finviz:') ? screen : liveViewRef.current.screen,
        showWatchlist,
        showOnlyUnticked,
      };
    }
    setMode(nextMode);
    if (nextMode === 'live') {
      setShowWatchlist(liveViewRef.current.showWatchlist);
      setShowOnlyUnticked(liveViewRef.current.showOnlyUnticked);
      if (!String(screen || '').startsWith('finviz:')) {
        setScreen(liveViewRef.current.screen || LIVE_DEFAULT_SCREEN);
      }
    } else {
      // Training: watchlist/unviewed filters have no meaning here, and the
      // backtest always trades the norgate momentum list.
      setShowWatchlist(false);
      setShowOnlyUnticked(false);
      if (String(screen || '').startsWith('finviz:')) setScreen(LIVE_DEFAULT_SCREEN);
    }
  };

  return (
    <div className="app-root live-only-app">
      <header className="app-toolbar live-only-toolbar">
        <button
          type="button"
          className="paper-user-input authenticated-user"
          aria-label="Open profile"
          title="Profile and risk per trade"
          onClick={onOpenProfile}
        >
          {username} ▾
        </button>
        <button type="button" className="toolbar-auth-button" onClick={onLogout}>Logout</button>

        <select className="page-select" aria-label="Page" value={page} onChange={(event) => changePage(event.target.value)}>
          <option value="screener">Scanner</option>
          <option value="trades">Trades</option>
          <option value="leaderboard">Leaders</option>
        </select>

        <button
          type="button"
          className={`toolbar-order-button ${orderOpen ? 'active' : ''}`}
          aria-pressed={orderOpen}
          onClick={() => setOrderOpen((current) => !current)}
          title={orderOpen ? 'Hide order box' : 'Show order box'}
        >
          Order
        </button>

        {mode === 'live' && (
          <>
            <button
              type="button"
              className="toolbar-order-button"
              onClick={toggleIbkrGateway}
              disabled={ibkrGatewayBusy || ibkrSyncBusy}
              title={ibkrGateway?.running
                ? 'Stop the IBKR gateway and end the live session'
                : 'Start the IBKR gateway (login takes around 30 seconds)'}
            >
              {ibkrGatewayBusy
                ? (ibkrGateway?.running ? 'Stopping…' : 'Starting…')
                : (ibkrGateway?.running ? 'Stop IBKR' : 'Start IBKR')}
            </button>
            <span
              className={`ibkr-gateway-badge ${ibkrGateway?.authenticated ? 'up' : 'down'}`}
              title={ibkrGateway?.running
                ? (ibkrGateway.authenticated ? 'Gateway is logged in' : 'Gateway is up but not logged in yet')
                : 'Gateway is stopped'}
            >
              {ibkrGateway?.running ? (ibkrGateway.authenticated ? 'IBKR live' : 'IBKR starting') : 'IBKR off'}
            </span>
            <button
              type="button"
              className="toolbar-order-button"
              onClick={syncIbkrHoldings}
              disabled={ibkrSyncBusy || paperBusy || ibkrGatewayBusy}
              title="Update Live holdings from IBKR; stops are preserved. Starts the gateway if it is stopped."
            >
              {ibkrSyncBusy ? 'Syncing…' : 'Sync IBKR'}
            </button>
          </>
        )}

        <span
          className={`market-direction-badge ${marketDirection?.direction === 'up' ? 'up' : 'down'}`}
          title={marketDirection
            ? `SPY 10MA ${marketDirection.direction === 'up' ? 'above' : 'below'} 20MA (${marketDirection.date})`
            : 'SPY market direction unavailable'}
        >
          {marketDirection
            ? (marketDirection.direction === 'up' ? 'Favorable Market' : 'Adverse Market')
            : 'Market —'}
        </span>

        <div className="account-strip" aria-label="Paper account">
          <span><small>Running</small><strong>${numberText(account.running_amount)} ({numberText(account.win_rate, 1)}%)</strong></span>
        </div>

        {page === 'screener' && (
          <>
            {/* Also offered with no active session: closed trades from an
                auto-released session linger in Holdings, and reset is the
                one action that clears them all at once. */}
            {mode === 'training' && (paperState.session || (paperState.orders || []).length > 0) && (
              <div className="session-control">
                <button type="button" disabled={paperBusy} onClick={resetPaperSession}>ResetAllTrades</button>
              </div>
            )}

            <div className="date-control">
              <label
                className="control-field date-picker-button"
                title="Choose a Finviz snapshot date"
              >
                <input
                  type="date"
                  aria-label="Trading date"
                  value={date}
                  onChange={(event) => {
                    const picked = event.target.value;
                    if (picked) {
                      setLastPickedDate(picked);
                      try { localStorage.setItem('screener2-last-picked-date', picked); } catch {}
                      // Persist per user so LstDt follows the account across
                      // devices; local copy is only an offline fallback.
                      apiFetch('/api/user/last-date', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ date: picked }),
                      }).catch(() => {});
                    }
                    requestDate(picked);
                  }}
                />
              </label>
            </div>

            {mode === 'live' && (
              <span className="live-only-badge" title="Live Finviz data">LIVE</span>
            )}

            <div className="control-group mode-toggle" aria-label="Mode">
              {[
                ...(liveAccess ? [['live', 'Live']] : []),
                ['training', 'Train'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={mode === value ? 'active' : ''}
                  onClick={() => changeMode(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <label className="control-group dollar-volume-filter" aria-label="Minimum dollar volume in millions">
              <span>$Vol≥</span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="M"
                value={minDollarVolumeM}
                onChange={(event) => setMinDollarVolumeM(event.target.value)}
              />
            </label>

            {mode === 'live' && (
            <div className="control-group screen-toggle" aria-label="Screen">
              <select
                className={`page-select finviz-group-select ${!showWatchlist ? 'active' : ''}`}
                aria-label="Live list"
                value={screen}
                onChange={(event) => { setShowWatchlist(false); changeScreen(event.target.value); }}
              >
                <option value={LIVE_DEFAULT_SCREEN}>norgate_momentum</option>
                <option value={LIVE_COILED_SCREEN}>norgate_coiled</option>
                <option value={LIVE_SPRINGOUT_SCREEN}>norgate_springout</option>
                {finvizGroups.map((group) => (
                  <option key={group.name} value={`finviz:${group.name}`}>{group.name}</option>
                ))}
              </select>
              {isNorgateCoiledScreen && coilAsOf && (
                <span className="coil-asof" title="The newest session's high and low are not final, so the coil is measured to the last settled session. The price column stays live from Finviz.">
                  coil to {coilAsOf}
                </span>
              )}
              {isLiveScreen && (
                <button
                  type="button"
                  className={showOnlyUnticked ? 'active' : ''}
                  aria-pressed={showOnlyUnticked}
                  onClick={() => setShowOnlyUnticked((current) => !current)}
                  title="Show only tickers not yet viewed today"
                >
                  New
                </button>
              )}
              {isLiveScreen && lastTicked && (
                <button
                  type="button"
                  className="toolbar-undo"
                  onClick={undoLastTick}
                  title={`Undo marking ${lastTicked} as viewed`}
                >
                  Undo {lastTicked}
                </button>
              )}
              <div className="ticker-search-wrap">
                <input
                  ref={searchInputRef}
                  className="ticker-search"
                  aria-label="Search ticker"
                  placeholder="Search ticker…"
                  value={tickerSearch}
                  onChange={(event) => setTickerSearch(event.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                />
                {searchFocused && searchResults.length > 0 && (() => {
                  const rect = searchInputRef.current?.getBoundingClientRect();
                  return (
                    <div
                      className="ticker-search-dropdown"
                      style={rect ? {
                        left: rect.left,
                        top: rect.bottom + 2,
                        minWidth: Math.max(rect.width, 130),
                      } : undefined}
                    >
                      {searchResults.map((symbol) => (
                        <button
                          key={symbol}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            selectScreenerSymbol(symbol);
                            setSearchFocused(false);
                          }}
                        >
                          {symbol}
                          {viewedSymbols.has(symbol) && <small> ✓</small>}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
            )}

          </>
        )}
      </header>

      {page === 'leaderboard' ? (
        <LeaderboardPage currentUsername={username} />
      ) : page === 'trades' ? (
        <TradesPage account={account} username={username.trim() || '—'} scrollTopRef={tradesScrollTopRef} />
      ) : page === 'vectorbt' ? (
        <VectorBTTransactionsPage
          onSelectSymbol={(trade) => {
            setMode('training');
            setMarket('US');
            setSelectedSymbol(String(trade.symbol || '').trim().toUpperCase());
            setDate(String(trade.entry_date || '').slice(0, 10));
            setPage('screener');
          }}
        />
      ) : (
      <main
        className="workspace"
        ref={workspaceRef}
        style={{ gridTemplateColumns: `${mainSplitPct}fr 7px ${100 - mainSplitPct}fr` }}
      >
        <section
          className="left-panel"
          ref={leftPanelRef}
          style={{ gridTemplateRows: `minmax(0, 1fr) 7px ${holdingsHeight}px` }}
        >
          {/* retainClosed is on in both modes: a closed trade stays struck
              through until it is released, so the chart stays reviewable and
              the row is dismissed deliberately rather than vanishing. */}
          <HoldingsTable
            showSymbols={isLiveScreen}
            orders={paperState.orders || []} onSelect={selectHolding}
            pctUpBySymbol={pctUpBySymbol}
            onClose={closeOrder} onCancel={cancelOrder} onUpdateStop={updateOrderStop} busy={paperBusy}
            onUpdateEntry={updateOrderEntry}
            onUpdateQty={updateOrderQty}
            onFill={fillOrder}
            entryEditable={mode === 'live'}
            stopPickEnabled={mode === 'live'}
            qtyEditable={mode === 'live'}
            retainClosed
            onReleaseTrade={releaseOrder}
            pickTarget={pickMode}
            onRequestPick={(orderId) => setPickMode((current) => (current === `stop:${orderId}` ? '' : `stop:${orderId}`))}
            onPrevious={() => moveScreenerSelection(-1)}
            onNext={() => moveScreenerSelection(1)}
            canPrevious={selectedSortedIndex > 0}
            canNext={selectedSortedIndex >= 0 && selectedSortedIndex < visibleRows.length - 1}
            navPosition={selectedSortedIndex >= 0 ? selectedSortedIndex + 1 : 0}
            navTotal={visibleRows.length}
          />
          <div className="resize-handle row" onMouseDown={startLeftRowResize} />
          <div className="chart-side">
            <div className="chart-view-controls">
              <div className="chart-view-toggle" aria-label="Chart type">
                <button
                  type="button"
                  className={chartMode === 'interactive' ? 'active' : ''}
                  onClick={() => setChartMode('interactive')}
                  title="TradingView interactive chart"
                >
                  TV
                </button>
                <button
                  type="button"
                  className={chartMode === 'chartdirector' ? 'active' : ''}
                  onClick={() => setChartMode('chartdirector')}
                  title="ChartDirector chart"
                >
                  CD
                </button>
              </div>
              <div className="chart-view-toggle" aria-label="Chart period">
                <button
                  type="button"
                  className={chartPeriod === 'daily' ? 'active' : ''}
                  onClick={() => setChartPeriod('daily')}
                  title="Daily chart"
                >
                  D
                </button>
                <button
                  type="button"
                  className={chartPeriod === 'monthly' ? 'active' : ''}
                  onClick={() => setChartPeriod('monthly')}
                  title="Monthly chart"
                >
                  M
                </button>
              </div>
              {chartMode === 'interactive' && (
                <button
                  type="button"
                  className={`chart-ma-toggle ${showMovingAverages ? 'active' : ''}`}
                  aria-pressed={showMovingAverages}
                  onClick={() => setShowMovingAverages((current) => !current)}
                  title="Show or hide moving averages"
                >
                  MA
                </button>
              )}
              <button
                type="button"
                className={`chart-watch-toggle ${watchlist.includes(selectedSymbol.trim().toUpperCase()) ? 'active' : ''}`}
                disabled={!selectedSymbol || watchlistBusy}
                onClick={toggleWatchlist}
                title={
                  watchlist.includes(selectedSymbol.trim().toUpperCase())
                    ? `Remove ${selectedSymbol.toUpperCase()} from watchlist`
                    : `Add ${selectedSymbol.toUpperCase()} to watchlist`
                }
              >
                *
              </button>
              {mode === 'live' && selectedSymbol && (
                <a
                  className="chart-finviz-link"
                  href={`https://finviz.com/quote.ashx?t=${encodeURIComponent(selectedSymbol)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  finvz
                </a>
              )}
            </div>
            {chartMode === 'interactive' ? (
              <InteractiveChart
                market={market}
                dataset={activeDataset}
                live={mode === 'live'}
                symbol={selectedSymbol}
                date={date}
                period={chartPeriod}
                pickMode={pickMode}
                onPricePick={handlePricePick}
                entryPrice={orderType === 'limit' ? limitPrice : ''}
                holdingEntryPrice={holdingEntryPrice}
                stopPrice={stopPrice}
                showMovingAverages={showMovingAverages}
              />
            ) : (
              <ChartDirectorChart
                market={market}
                dataset={activeDataset}
                live={mode === 'live'}
                symbol={selectedSymbol}
                date={date}
                period={chartPeriod}
                pickMode={pickMode}
                onPricePick={handlePricePick}
                transactionMarkers={transactionMarkers}
                fullHistory={reviewingClosedTrade}
                reviewEndDate={reviewEndDate}
                reviewStartDate={reviewStartDate}
              />
            )}
          </div>
        </section>

        {/* Floating Finviz shortcut - draggable, position persists. Hidden in
            training mode: looking the symbol up on Finviz shows its live state,
            which is hindsight for a trade being replayed on a past date. Live
            mode keeps it. */}
        {mode !== 'training' && (
          <DraggableFab
            label="Fin"
            className="floating-fin-button"
            storageKey="screener2-fin-fab-pos"
            disabled={!selectedSymbol}
            onAction={() => window.open(`https://finviz.com/quote.ashx?t=${encodeURIComponent(selectedSymbol.trim().toUpperCase())}`, '_blank', 'noopener')}
          />
        )}

        {/* Training only: jump back to the last calendar-picked date. */}
        {mode === 'training' && (
          <DraggableFab
            label="LstDt"
            className="floating-lstdt-button"
            storageKey="screener2-lstdt-fab-pos-v2"
            title={lastTradeDate
              ? `Jump to ${lastTradeDate} - the day of your most recent trade`
              : 'No training purchase date is available'}
            disabled={!lstDtTarget || lstDtTarget === date}
            onAction={() => {
              // Keep the date picker in sync immediately; the session update
              // below is asynchronous and may otherwise repaint the old date.
              setDate(lstDtTarget);
              requestDate(lstDtTarget);
            }}
          />
        )}

        {/* Training only: advance one trading day so prices rebuild. */}
        {mode === 'training' && (
          <DraggableFab
            label="FwdDate"
            className="floating-nextday-button"
            storageKey="screener2-nextday-fab-pos-v2"
            title={date
              ? `Forward one trading day from ${date} (prices + backtest update)`
              : 'Forward one trading day'}
            disabled={!date || paperBusy}
            onAction={() => { moveDate(1); }}
          />
        )}

        <div className="resize-handle col" onMouseDown={startColResize} />

        <section
          className="table-panel"
          ref={rightPanelRef}
          style={{ gridTemplateRows: 'minmax(0, 1fr)' }}
        >
          {orderOpen && (
          <OrderPanel
            symbol={selectedSymbol} market={market} marketPrice={selectedRow?.close}
            orderType={orderType} setOrderType={setOrderType}
            quantity={quantity} setQuantity={setQuantity}
            riskPerTrade={activeRiskPerTrade}
            limitPrice={limitPrice} setLimitPrice={setLimitPrice}
            stopPrice={stopPrice} setStopPrice={setStopPrice}
            pickMode={pickMode} setPickMode={setPickMode}
            onPlace={placeOrder} busy={paperBusy} error={paperError}
            alreadyOwned={alreadyOwned}
            tradingMode={mode}
            onClose={() => setOrderOpen(false)}
            ibkrTrading={ibkrTrading}
            ibkrAccounts={ibkrAccounts}
            ibkrAccount={ibkrAccount} setIbkrAccount={setIbkrAccount}
            ibkrOrderType={ibkrOrderType} setIbkrOrderType={setIbkrOrderType}
            ibkrLimitPrice={ibkrLimitPrice} setIbkrLimitPrice={setIbkrLimitPrice}
            onIbkrOrder={placeIbkrOrder} ibkrBusy={ibkrOrderBusy}
            ibkrError={ibkrOrderError} ibkrResult={ibkrOrderResult}
          />
          )}
          <section className="screener-section">
            {error && <div className="table-message error">{error}</div>}
            {loading && (
              <div className="table-message">
                {generating ? `Generating screens for ${date}…` : `Loading ${date}…`}
              </div>
            )}
            {!loading && !error && rows.length === 0 && <div className="table-message">No matches in this screen</div>}
            {rows.length > 0 && (
              <div
                className="table-scroll"
                ref={tableScrollRef}
                onScroll={(event) => rememberScreenerView({ scrollTop: event.currentTarget.scrollTop })}
              >
              <table className="results-table">
                <thead>
                  <tr>
                    <th className="col-symbol" onClick={() => changeSort('symbol')}>Symbol{sortMark('symbol')}</th>
                    <th className="number col-up" onClick={() => changeSort('pct_up')}>%Up{sortMark('pct_up')}</th>
                    <th className="number col-1m" onClick={() => changeSort('pct_from_low')}>
                      1M%{sortMark('pct_from_low')}
                    </th>
                    <th className="number col-adr" onClick={() => changeSort('adr')}>ADR{sortMark('adr')}</th>
                    <th
                      className="number col-atrsl"
                      title="ATR as % of current price / ATR remaining after today's absolute % move"
                      onClick={() => changeSort('sl')}
                    >ATR%/SL%{sortMark('sl')}</th>
                    <th
                      className="number col-mcap"
                      title="Market capitalisation from the Finviz feed"
                      onClick={() => changeSort('market_cap')}
                    >
                      Market Cap{sortMark('market_cap')}
                    </th>
                    <th className="number col-volume" onClick={() => changeSort('volume')}>Volume{sortMark('volume')}</th>
                    <th className="number col-rvol"
                      title="Relative volume: how much this stock has traded against what it normally trades BY THIS POINT IN THE SESSION. While the market is open that is today's volume so far against the median for the same 5-minute slice of previous sessions; otherwise it is the last completed session against the median session. 1.0x is a normal day."
                      onClick={() => changeSort('rvol')}>RVol{sortMark('rvol')}</th>
                    {isNorgateSpringoutScreen && (
                      <>
                        <th className="number col-range"
                          title="The spring candle: the advance out of the coil measured in ATRs, closing back above the 10MA. A dot marks today's still-forming candle. A dash means the name is still coiling."
                          onClick={() => changeSort('spring_atrs')}>Spring{sortMark('spring_atrs')}</th>
                        <th className="number col-range"
                          title="The run: sessions in the 30 before the coil that closed above the 10MA, and how far above the line it got at its peak. Counted, not consecutive - 3 or more qualifies."
                          onClick={() => changeSort('ext_days')}>Ext{sortMark('ext_days')}</th>
                        <th className="number col-range"
                          title="The return to the line: consecutive sessions closing within +1% / -10% of the 10MA, or trading through it. 2 or more qualifies. T = sessions that traded through the line, B = sessions that closed below it."
                          onClick={() => changeSort('coil_days')}>Coil{sortMark('coil_days')}</th>
                        <th className="number col-range"
                          title="Base quality, reported but never used to filter. Contraction = average true range during the coil against the extension leg: under 1.00x means the stock went quiet while it hugged the line, which is what a real base looks like. q = sessions in the coil whose body was 30% or less of the stock's normal pre-coil range - the nothing days."
                          onClick={() => changeSort('coil_contraction')}>Base{sortMark('coil_contraction')}</th>
                      </>
                    )}
                    {/* Only the coiled list carries these, and without them
                        there is no way to see why a row qualified. */}
                    {isNorgateCoiledScreen && (
                      <>
                        <th className="number col-range" onClick={() => changeSort('range_1d')}
                          title="High-to-low range of the tightest two sessions in the last 10, as a % of that day's close. They need not be consecutive."
                        >Coil days{sortMark('range_1d')}</th>
                        <th className="number col-range" onClick={() => changeSort('range_ratio')}
                          title="The quieter of those two days against this stock's own 20-day average range. Lower is tighter."
                        >Coil{sortMark('range_ratio')}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => (
                    <tr
                      key={row.symbol}
                      className={[
                        row.symbol === selectedSymbol ? 'selected' : '',
                        isLiveScreen && viewedSymbols.has(row.symbol) ? 'row-viewed' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => selectScreenerSymbol(row.symbol)}
                    >
                      <td
                        className={`symbol col-symbol${isLiveScreen && row.earnings_soon ? ' earnings-soon' : ''}`}
                        title={row.earnings_soon ? 'Earnings due (Finviz: next 5 days)' : undefined}
                      >
                        {isLiveScreen && (
                          <button
                            type="button"
                            className={`tick-btn ${viewedSymbols.has(row.symbol) ? 'ticked' : ''}`}
                            aria-pressed={viewedSymbols.has(row.symbol)}
                            title={viewedSymbols.has(row.symbol) ? 'Mark as not viewed' : 'Mark as viewed'}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleViewed(row.symbol);
                            }}
                          >
                            {viewedSymbols.has(row.symbol) ? '✓' : '□'}
                          </button>
                        )}
                        {isLiveScreen || showWatchlist ? row.symbol : blindSymbolText(row.symbol)}
                      </td>
                      <td className={`number col-up ${row.pct_up == null ? '' : row.pct_up >= 0 ? 'positive' : 'negative'}`}>
                        {row.pct_up == null ? '—' : `${row.pct_up >= 0 ? '+' : ''}${numberText(row.pct_up)}%`}
                      </td>
                      <td className="number col-1m">{numberText(row[fromLowField], 0)}%</td>
                      <td className="number col-adr">
                        {row.adr == null || row.adr === '' ? '—' : numberText(Math.abs(Number(row.adr)), 1)}
                      </td>
                      <td className="number atr-sl-cell col-atrsl">
                        <span>{percentText(atrPercent(row), 1)}</span>
                        <span className="atr-sl-separator">/</span>
                        <span className={(remainingAtrPercent(row) ?? 0) >= 0 ? 'positive' : 'negative'}>
                          {percentText(remainingAtrPercent(row), 1)}
                        </span>
                      </td>
                      <td className="number col-mcap">
                        {marketCapText(row.actual_market_cap ?? row.market_cap)}
                      </td>
                      <td className="number col-volume">{volumeText(row.volume)}</td>
                      <td className={`number col-rvol${Number(row.rvol) >= 2 ? ' rvol-heavy' : ''}`}
                        title={row.rvol == null
                          ? 'No volume baseline for this stock yet'
                          : row.rvol_stage === 'live'
                            ? `${row.rvol}x normal volume by this point in today's session`
                            : `${row.rvol}x the median session, measured on the last completed day`}>
                        {row.rvol == null ? '—' : `${Number(row.rvol).toFixed(1)}x`}
                      </td>
                      {isNorgateSpringoutScreen && (
                        <>
                          <td className={`number col-range${row.spring ? ' spring-fired' : ''}`}
                            title={row.spring
                              ? `Spring on ${row.spring_date} (${row.spring_stage === 'live' ? "today's candle, still forming" : 'settled close'}): +${row.spring_pct}%, ${row.spring_atrs} ATR, closing ${row.spring_above_ma_pct}% above the 10MA`
                                + `${row.spring_rvol != null ? `, ${row.spring_rvol}x relative volume${row.spring_rvol_ok === false ? ' (light - not a disqualifier)' : ''}` : ', relative volume unknown'}`
                              : 'Still coiling - no spring candle yet'}>
                            {row.spring ? `${Number(row.spring_atrs).toFixed(1)}x${row.spring_stage === 'live' ? ' •' : ''}` : '—'}
                          </td>
                          <td className="number col-range"
                            title={row.ext_from ? `${row.ext_days} sessions above the 10MA between ${row.ext_from} and ${row.ext_to} (longest unbroken run ${row.ext_longest_run}), peaking ${row.ext_max_pct}% above the line` : ''}>
                            {row.ext_days != null ? `${row.ext_days}d +${Number(row.ext_max_pct).toFixed(1)}%` : '—'}
                          </td>
                          <td className="number col-range"
                            title={`10MA ${row.ma10 ?? '—'}; coiled ${row.coil_from ?? '—'} to ${row.coil_to ?? '—'}, ${row.coil_touch_days ?? 0} traded through the line, ${row.coil_below_days ?? 0} closed below it`}>
                            {row.coil_days != null ? `${row.coil_days}d${row.coil_touch_days ? ` / ${row.coil_touch_days}T` : ''}${row.coil_below_days ? ` / ${row.coil_below_days}B` : ''}` : '—'}
                          </td>
                          <td className="number col-range"
                            title={row.coil_contraction != null
                              ? `Coil ATR ${row.coil_atr_pct}% of price vs ${row.ext_atr_pct}% on the extension leg`
                                + ` (${Number(row.coil_contraction).toFixed(2)}x), ${row.coil_tiny_days ?? 0} near-bodyless sessions`
                              : 'No contraction reading'}>
                            {row.coil_contraction != null
                              ? `${Number(row.coil_contraction).toFixed(2)}x${row.coil_tiny_days ? ` / ${row.coil_tiny_days}q` : ''}`
                              : '—'}
                          </td>
                        </>
                      )}
                      {isNorgateCoiledScreen && (
                        <>
                          <td className="number col-range"
                            title={row.coil_from
                              ? `Tightest sessions: ${row.coil_from}${row.coil_to && row.coil_to !== row.coil_from ? ` and ${row.coil_to}` : ''}`
                              : ''}>
                            {(row.coil_ranges || [row.range_1d, row.range_2d])
                              .filter((value) => value != null)
                              .map((value) => `${value}%`).join(' / ') || '—'}
                          </td>
                          <td className="number col-range" title={`20-day average range ${percentText(row.average_range, 1)}`}>
                            {row.range_ratio != null ? `${Number(row.range_ratio).toFixed(2)}x` : '—'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </section>
        </section>
      </main>
      )}
    </div>
  );
};

export default MainApp;
